import React, { useEffect, useRef, useState } from "react";
import Map from "@arcgis/core/Map";
import SceneView from "@arcgis/core/views/SceneView"; // 3D
import GeoJSONLayer from "@arcgis/core/layers/GeoJSONLayer";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Sketch from "@arcgis/core/widgets/Sketch";
import Search from "@arcgis/core/widgets/Search";
import * as projection from "@arcgis/core/geometry/projection"; // ✅ FIX

function TestMapping() {
  const mapRef = useRef(null);
  const viewRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    if (!mapRef.current) return;

    const map = new Map({
      basemap: "streets-vector",
    });

    // ✅ 3D SceneView
    const view = new SceneView({
      container: mapRef.current,
      map,
      center: [121.0437, 14.6760], // Example: Quezon City
      zoom: 15,
    });

    viewRef.current = view;

    // ✅ Django GeoJSON endpoint
    const geojsonUrl = `${process.env.REACT_APP_API_URL}/properties/`;

    // ✅ Load projection module before adding layers
    projection.load().then(() => {
      console.log("Projection module ready ✔");

      const parcelsLayer = new GeoJSONLayer({
        url: geojsonUrl,
        outFields: ["*"],
        popupTemplate: {
          title: "Property: {tct_number}",
          content: `
            <b>Owner:</b> {owner}<br/>
            <b>Address:</b> {address}<br/>
            <b>Area:</b> {area} sqm<br/>
            <b>Floors:</b> {floors}
          `
        },
        renderer: {
          type: "simple",
          symbol: {
            type: "polygon-3d",
            symbolLayers: [
              {
                type: "extrude",
                material: { color: "lightblue" },
                size: {
                  // if height is stored, use {field: "height"} instead
                  valueExpression: "$feature.floors * 3" // 3m per floor
                }
              }
            ]
          }
        }
      });

      map.add(parcelsLayer);

      // ✅ Sketch Layer
      const graphicsLayer = new GraphicsLayer();
      map.add(graphicsLayer);

      const sketch = new Sketch({
        view,
        layer: graphicsLayer,
        creationMode: "update"
      });
      view.ui.add(sketch, "top-right");

      // ✅ Search widget (address + TCT search)
      const searchWidget = new Search({
        view,
        includeDefaultSources: true,
        sources: [
          {
            layer: parcelsLayer,
            searchFields: ["tct_number", "owner"],
            displayField: "tct_number",
            exactMatch: false,
            outFields: ["*"],
            name: "Property Records",
            placeholder: "Search TCT / Owner"
          }
        ]
      });

      view.ui.add(searchWidget, "top-left");

      // ✅ On polygon creation
      sketch.on("create", async (event) => {
        if (event.state === "complete" && event.graphic.geometry.type === "polygon") {
          let geom = event.graphic.geometry;

          // ✅ Ensure geometry is in WGS84 (EPSG:4326) before sending to Django
          if (projection.isLoaded()) {
            try {
              geom = projection.project(geom, { wkid: 4326 });
            } catch (err) {
              console.warn("Projection failed, sending raw geom:", err);
            }
          }

          const coordinates = geom.rings.map(ring => ring.map(pt => [pt[0], pt[1]]));

          const feature = {
            type: "Feature",
            geometry: {
              type: "Polygon",
              coordinates: coordinates
            }
          };

          // Ask user for details
          const tct = prompt("Enter TCT number (e.g. TCT-001):");
          if (!tct) {
            alert("Cancelled (TCT required).");
            graphicsLayer.remove(event.graphic);
            return;
          }
          const owner = prompt("Owner name (optional):", "");
          const floors = parseInt(prompt("Number of floors (default 1):", "1")) || 1;
          const area = parseFloat(prompt("Area sqm (optional):", "")) || null;

          try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/properties/`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                tct_number: tct,
                owner: owner,
                floors: floors,
                area: area,
                geometry: feature.geometry // ✅ must match GeoJSON standard
              }),
            });

            if (!response.ok) {
              const err = await response.json();
              alert("Server error: " + (err.detail || JSON.stringify(err)));
              return;
            }

            alert("Property created. It will appear on the map.");
            parcelsLayer.refresh(); // ✅ refresh the GeoJSON layer
            graphicsLayer.remove(event.graphic);
          } catch (e) {
            alert("Network error: " + e.message);
          }
        }
      });
    });

    view.when(() => setMapLoaded(true));

    return () => {
      if (view) {
        view.destroy();
      }
    };
  }, []);

  return <div style={{ width: "100%", height: "100vh" }} ref={mapRef} />;
}

export default TestMapping;
